import { useEffect, useRef, useState } from "react";
import { Alert, Button, EmptyView, ErrorState, Field, LoadingState, Select, Tag } from "@/shared/rd";
import { keys } from "@/lib/i18n/portal";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import {
  capturePrivateSession,
  isPrivateSessionCurrent,
} from "@/lib/api/private-session";
import {
  initialSelection,
  resolveSelection,
  sanitizedJson,
  selectionKey,
  type SimulatorSelection,
} from "../services/selection";
import {
  useSimulatorCatalog,
  useSimulatorPreview,
  useSimulatorRun,
} from "../services/useSimulator";
import type { SimulatorPreview, SimulatorRun } from "../types/simulator.types";
import { SimulationResult } from "../components/SimulationResult";
import { RealSimulatorPanel } from "../components/RealSimulatorPanel";
import { SimCard } from "../components/SimulatorParts";
import { preStyle } from "../components/simulator-styles";
const copy = keys.integrationSimulator;
type Action = "preview" | "run";
export function IsolatedSimulatorPanel() {
  const t = useTranslations();
  const locale = useLocale();
  const catalog = useSimulatorCatalog();
  const preview = useSimulatorPreview();
  const run = useSimulatorRun();
  const [lifetime] = useState(capturePrivateSession);
  const [selection, setSelection] = useState(initialSelection);
  const [previewResult, setPreviewResult] = useState<{
    key: string;
    data: SimulatorPreview;
  }>();
  const [runResult, setRunResult] = useState<{
    key: string;
    data: SimulatorRun;
  }>();
  const [pending, setPending] = useState<Action>();
  const [failure, setFailure] = useState<Action>();
  const version = useRef(0);
  const busy = useRef(false);
  const resultHeading = useRef<HTMLHeadingElement>(null);
  const input = resolveSelection(catalog.data, selection);
  const key = selectionKey(input);
  const provider = catalog.data?.providers.find(
    (provider) => provider.provider === input?.provider,
  );
  const scenario = provider?.scenarios.find(
    (scenario) => scenario.scenario === input?.scenario,
  );
  const providers =
    catalog.data?.providers.filter(
      (provider) => provider.category === selection.category,
    ) ?? [];
  const visiblePreview =
    previewResult?.key === key ? previewResult.data : undefined;
  const visibleRun = runResult?.key === key ? runResult.data : undefined;
  useEffect(() => {
    if (visibleRun) resultHeading.current?.focus();
  }, [visibleRun]);
  function change(next: SimulatorSelection) {
    version.current++;
    setSelection(next);
    setPreviewResult(undefined);
    setRunResult(undefined);
    setFailure(undefined);
    preview.reset();
    run.reset();
  }
  async function submit(action: Action) {
    if (!input || busy.current || !isPrivateSessionCurrent(lifetime)) return;
    busy.current = true;
    const requestVersion = ++version.current;
    setPending(action);
    setFailure(undefined);
    if (action === "run") setRunResult(undefined);
    try {
      const response = await (action === "preview"
        ? preview.mutateAsync(input)
        : run.mutateAsync(input));
      if (
        version.current !== requestVersion ||
        !isPrivateSessionCurrent(lifetime)
      )
        return;
      if (response.mode !== "isolated" || selectionKey(response) !== key)
        throw new Error("Unexpected simulator result");
      // The executed response is authoritative. No second POST or read is needed to refresh this isolated fixture.
      if (action === "preview")
        setPreviewResult({ key, data: response as SimulatorPreview });
      else setRunResult({ key, data: response as SimulatorRun });
    } catch {
      if (
        version.current === requestVersion &&
        isPrivateSessionCurrent(lifetime)
      )
        setFailure(action);
    } finally {
      busy.current = false;
      if (isPrivateSessionCurrent(lifetime)) setPending(undefined);
    }
  }
  const page = t(copy.title).toLocaleLowerCase(locale);
  if (catalog.isLoading)
    return <LoadingState title={t(keys.shell.loadingTitle, { page })} sub={t(keys.shell.loadingSub)} rows={0} />;
  if (catalog.error || !catalog.data)
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page })}
        sub={t(copy.loadFailed)}
        onRetry={() => void catalog.refetch()}
        retryLabel={catalog.isRefetching ? t(keys.shell.retrying) : t(keys.shell.retry)}
        retrying={catalog.isRefetching}
      />
    );
  return (
    <section
      aria-label={t(copy.title)}
      style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0, maxWidth: 960 }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div>
          <Tag tone="blue">{t(copy.isolated)}</Tag>
        </div>
        <p style={{ margin: 0, fontSize: 14 }}>{t(copy.intro)}</p>
        <p style={{ margin: 0, fontSize: 13, color: "var(--rd-text-tertiary)" }}>
          {t(copy.isolationNote)}
        </p>
      </div>
      <SimCard title={t(copy.setup)}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 14,
          }}
        >
          <Field label={t(copy.category)}>
            <Select
              aria-label={t(copy.category)}
              value={selection.category}
              onChange={(event) =>
                change({
                  ...initialSelection,
                  category:
                    event.target.value === "shipping" ? "shipping" : "payment",
                })
              }
            >
              <option value="payment">{t(copy.categories.payment)}</option>
              <option value="shipping">{t(copy.categories.shipping)}</option>
            </Select>
          </Field>
          <Field label={t(copy.provider)}>
            <Select
              aria-label={t(copy.provider)}
              value={input?.provider ?? ""}
              disabled={!providers.length}
              onChange={(event) =>
                change({
                  ...selection,
                  provider: event.target.value,
                  scenario: "",
                  case: "",
                })
              }
            >
              {providers.map((provider) => (
                <option key={provider.provider} value={provider.provider}>
                  {t(copy.providers[provider.provider] ?? copy.unknownCode, {
                    code: provider.provider,
                  })}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t(copy.scenario)}>
            <Select
              aria-label={t(copy.scenario)}
              value={input?.scenario ?? ""}
              disabled={!scenario}
              onChange={(event) =>
                change({
                  ...selection,
                  provider: input?.provider ?? "",
                  scenario: event.target.value,
                  case: "",
                })
              }
            >
              {provider?.scenarios.map((scenario) => (
                <option key={scenario.scenario} value={scenario.scenario}>
                  {t(
                    (copy.scenarios as Record<string, string>)[
                      scenario.scenario
                    ] ?? copy.unknownCode,
                    { code: scenario.scenario },
                  )}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t(copy.case)}>
            <Select
              aria-label={t(copy.case)}
              value={input?.case ?? ""}
              disabled={!scenario}
              onChange={(event) =>
                change({
                  ...selection,
                  provider: input?.provider ?? "",
                  scenario: input?.scenario ?? "",
                  case: event.target.value,
                })
              }
            >
              {scenario?.cases.map((caseValue) => (
                <option key={caseValue} value={caseValue}>
                  {t(
                    (copy.cases as Record<string, string>)[caseValue] ??
                      copy.unknownCode,
                    { code: caseValue },
                  )}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {provider ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              fontSize: 13,
              color: "var(--rd-text-tertiary)",
            }}
          >
            <div>
              <Tag
                tone={
                  provider.capability ===
                  "defensive_webhook_stub_application_path"
                    ? "amber"
                    : "grey"
                }
              >
                {t(
                  provider.capability ===
                    "defensive_webhook_stub_application_path"
                    ? copy.stubPath
                    : copy.applicationPath,
                )}
              </Tag>
            </div>
            {provider.capability ===
            "defensive_webhook_stub_application_path" ? (
              <p style={{ margin: 0 }}>{t(copy.stubNote)}</p>
            ) : null}
            {provider.externalLookup !== "none" ? (
              <p style={{ margin: 0 }}>{t(copy.lookupNote)}</p>
            ) : null}
            {provider.provider === "tap" ? (
              <p style={{ margin: 0 }}>{t(copy.advisoryNote)}</p>
            ) : null}
            {provider.unsupportedScenarios.length ? (
              <p style={{ margin: 0 }}>
                {t(copy.unsupported, {
                  statuses: provider.unsupportedScenarios
                    .map((status) =>
                      t(
                        (copy.scenarios as Record<string, string>)[status] ??
                          copy.unknownCode,
                        { code: status },
                      ),
                    )
                    .join("، "),
                })}
              </p>
            ) : null}
          </div>
        ) : (
          <EmptyView illustration="admin-test-empty" title={t("integrationSimulator.catalogEmptyTitle")} sub={t(copy.catalogEmpty)} />
        )}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!input || (Boolean(pending) && pending !== "preview")}
            loading={pending === "preview"}
            loadingLabel={t(copy.previewing)}
            onClick={() => void submit("preview")}
          >
            {t(copy.preview)}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={!input || (Boolean(pending) && pending !== "run")}
            loading={pending === "run"}
            loadingLabel={t(copy.running)}
            onClick={() => void submit("run")}
          >
            {t(visibleRun ? copy.runAgain : copy.run)}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            muted
            onClick={() => change(initialSelection)}
          >
            {t(copy.reset)}
          </Button>
        </div>
        {failure ? (
          <Alert
            tone="error"
            title={t(failure === "run" ? "integrationSimulator.runFailedTitle" : "integrationSimulator.previewFailedTitle")}
            action={
              <Button
                variant="secondary"
                size="sm"
                loading={Boolean(pending)}
                loadingLabel={t(keys.shell.retrying)}
                onClick={() => void submit(failure)}
              >
                {t(keys.shell.retry)}
              </Button>
            }
          >
            {t(copy.requestFailed)}
          </Alert>
        ) : null}
      </SimCard>
      {visiblePreview ? (
        <SimCard title={t(copy.previewTitle)} note={t(copy.previewHint)}>
          {visiblePreview.requests.map((request) => (
            <div key={request.sequence} style={{ minWidth: 0 }}>
              <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 500 }}>
                {t(copy.request, { number: request.sequence })}
              </h4>
              <pre dir="ltr" style={{ ...preStyle, maxHeight: 320 }}>
                {sanitizedJson(request)}
              </pre>
            </div>
          ))}
          <details style={{ fontSize: 13 }}>
            <summary style={{ cursor: "pointer", color: "var(--rd-text-secondary)" }}>
              {t(copy.expected)}
            </summary>
            <pre dir="ltr" style={{ ...preStyle, maxHeight: 192, marginTop: 8 }}>
              {sanitizedJson(visiblePreview.expected)}
            </pre>
          </details>
        </SimCard>
      ) : null}
      {visibleRun ? (
        <div aria-live="polite" style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
          <h2
            ref={resultHeading}
            tabIndex={-1}
            style={{ margin: 0, fontSize: 15, fontWeight: 600, outline: "none" }}
          >
            {t(copy.resultTitle)}
          </h2>
          <SimulationResult result={visibleRun} />
        </div>
      ) : null}
      <SimCard title={t(copy.limits)}>
        <ul
          style={{
            margin: 0,
            paddingInlineStart: 20,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            fontSize: 13,
            color: "var(--rd-text-tertiary)",
          }}
        >
          <li>{t(copy.databaseLimit)}</li>
          <li>{t(copy.networkLimit)}</li>
          <li>{t(copy.deliveryLimit)}</li>
        </ul>
      </SimCard>
    </section>
  );
}

export function IntegrationSimulatorPage() {
  return <RealSimulatorPanel />;
}
